/**
 * Reverse an invoice's CURRENT (post-credit) `total` back to what it was
 * BILLED before any account credit was applied. Account credit is a PAYMENT
 * (or, for a goodwill grant, a price concession) — it is never a reduction in
 * the WORK billed against an estimate. Every "billed against this estimate"
 * figure (remaining-to-bill, % invoiced, the convert guard, status previews)
 * must count invoices at this pre-credit value; balance-due / amount-owed /
 * revenue figures stay on the net `total` and must NOT use this helper.
 *
 * Mirrors the server's `release_customer_credit_from_invoice` restore math
 * (supabase/migrations/20260808150000_credit_tax_buckets.sql) — the client
 * and server MUST agree to the cent, or the UI can offer to bill money the
 * server will refuse.
 *
 *   credit = account_credit_applied ?? 0
 *   credit === 0                                    -> total
 *   credit_applied_pre_tax === true AND taxable_base/tax_amount/tax_rate all
 *   present:
 *     b   = taxable_base + (credit_applied_concession ?? 0)   // restored base
 *     tax = round2(b * tax_rate)                               // recomputed
 *     ->  round2(b + tax)
 *   otherwise                                        -> round2(total + credit)
 *
 * All money math is done in INTEGER CENTS to match Postgres
 * `ROUND(numeric, 2)` (half away from zero) exactly — floating-point dollar
 * arithmetic cannot be trusted to reproduce that rounding on tie cases
 * (e.g. a computed tax of exactly $0.125).
 *
 * `null` / `undefined` / `''` on an input field means ABSENT and is never
 * silently treated as 0 — the two exceptions are `account_credit_applied`
 * and `credit_applied_concession`, which the formula itself defaults to 0
 * (a row with no credit ever applied legitimately has nothing there).
 *
 * @param {object} invoice - a row (or partial row) from `invoices`. Expected
 *   fields: total, account_credit_applied, credit_applied_concession,
 *   credit_applied_pre_tax, taxable_base, tax_amount, tax_rate. Values may be
 *   numbers or numeric strings, as PostgREST returns `numeric` columns.
 * @returns {number} dollars, rounded to 2 decimals.
 */

const isAbsent = (v) => v === null || v === undefined || v === '';

/** PostgREST numeric field (number or numeric string) -> Number, or null when absent/invalid. */
function toNumberOrNull(v) {
  if (isAbsent(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Dollars -> integer cents (nearest cent), or null when the input is absent/invalid. */
function dollarsToCents(v) {
  const n = toNumberOrNull(v);
  return n === null ? null : Math.round(n * 100);
}

/**
 * scaled / divisor rounded to the nearest integer, ties AWAY from zero —
 * this is what Postgres `ROUND(numeric, 2)` does (proven in
 * 20260808150000's header: 0.125 -> 0.13, not banker's-rounding 0.12).
 * `divisor` is always a positive integer.
 */
function roundHalfAwayFromZero(scaled, divisor) {
  const half = divisor / 2;
  if (scaled >= 0) return Math.floor((scaled + half) / divisor);
  return -Math.floor((-scaled + half) / divisor);
}

export function invoicePreCreditTotal(invoice) {
  const totalCents = dollarsToCents(invoice?.total) ?? 0;
  const creditCents = dollarsToCents(invoice?.account_credit_applied) ?? 0;

  if (creditCents === 0) {
    return totalCents / 100;
  }

  const preTax = invoice?.credit_applied_pre_tax === true;
  const taxableBaseCents = dollarsToCents(invoice?.taxable_base);
  const taxAmountCents = dollarsToCents(invoice?.tax_amount);
  const taxRate = toNumberOrNull(invoice?.tax_rate);

  if (preTax && taxableBaseCents !== null && taxAmountCents !== null && taxRate !== null) {
    const concessionCents = dollarsToCents(invoice?.credit_applied_concession) ?? 0;
    const bCents = taxableBaseCents + concessionCents;
    // tax_rate is numeric(6,4) — an integer 1e-4 unit avoids float drift in
    // the multiply, matching the server's numeric(6,4) x numeric(10,2) math.
    const rateE4 = Math.round(taxRate * 10000);
    const taxCents = roundHalfAwayFromZero(bCents * rateE4, 10000);
    return (bCents + taxCents) / 100;
  }

  return (totalCents + creditCents) / 100;
}

export default invoicePreCreditTotal;
